import React, { useState, useEffect } from "react";
import Joyride, { STATUS, ACTIONS, EVENTS } from "react-joyride";
import { Collapsible } from "../ui/collapsible";
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "../ui/sidebarNew";
import { useNavigate, useLocation } from "react-router-dom";
import { appointmentsService } from "../../Services/appointments.service";
import {
  decodeFromBase64,
  hasPermission,
  parseToJson,
} from "../Constants/AppConstant";
import ModalNotSubscribe from "../Base/Common/Modal/ModalNotSubscribe";

// Custom styles for Joyride tour
const joyrideStyles = {
  options: {
    primaryColor: "#10b981",
    backgroundColor: "#ffffff",
    textColor: "#1e293b",
    overlayColor: "rgba(0, 0, 0, 0.5)", // Dark overlay background
    spotlightShadow: "0 0 0 0 transparent", // No spotlight shadow
    beaconSize: 36,
    zIndex: 10000,
  },
  tooltip: {
    borderRadius: "12px",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
    padding: "20px",
  },
  tooltipContainer: {
    textAlign: "left",
  },
  tooltipTitle: {
    fontSize: "18px",
    fontWeight: "600",
    marginBottom: "8px",
    color: "#059669",
  },
  tooltipContent: {
    fontSize: "14px",
    lineHeight: "1.6",
    color: "#475569",
  },
  buttonNext: {
    backgroundColor: "#10b981",
    borderRadius: "8px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "500",
  },
  buttonBack: {
    color: "#64748b",
    marginRight: "10px",
  },
  buttonSkip: {
    color: "#94a3b8",
  },
  spotlight: {
    display: "none", // Hide spotlight completely
  },
};

export function NavMain({ items, user, userInfo }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isBrand = user?.data?.active_role === 3;
  const [isSubscribeModal, setIsSubscribeModal] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [tourSteps, setTourSteps] = useState([]);


  // Check if profile sections are incomplete and set up tour
  useEffect(() => {
    if (!userInfo || isBrand) return;

    const steps = [];
    const isProfileIncomplete = userInfo.i_profile_set === 0;
    const isRateIncomplete = userInfo.rate_set === 0;

    // Only show tour if at least one section is incomplete
    if (isProfileIncomplete || isRateIncomplete) {
      // Welcome step
      steps.push({
        target: "body",
        content: "Welcome! Let's complete your profile to get started. A complete profile helps brands discover you and increases your chances of getting campaigns.",
        placement: "center",
        disableBeacon: true,
        title: "🎉 Complete Your Profile",
      });

      if (isProfileIncomplete) {
        steps.push({
          target: '[data-tour="public-profile"]',
          content: "Start by setting up your Public Profile. Add your bio, location, and other details to help brands learn more about you.",
          placement: "right",
          title: "📝 Public Profile",
          disableOverlay: true,
        });
      }

      if (isRateIncomplete) {
        steps.push({
          target: '[data-tour="rate-media-kit"]',
          content: "Set up your rates and media kit to showcase your pricing and portfolio to potential brand partners.",
          placement: "right",
          title: "💰 Rate & Media Kit",
          disableOverlay: true,
        });
      }
    }

    if (steps.length > 0) {
      setTourSteps(steps);
      // Delay tour start to ensure DOM elements are rendered
      const timer = setTimeout(() => {
        setRunTour(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [userInfo, isBrand]);

  // Handle tour callback
  const handleJoyrideCallback = (data) => {
    const { status, action, type, index, step } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      setRunTour(false);
      // Removed sessionStorage - tour will show on every login if profile sections incomplete
    }

    // Navigate on spotlight click for specific steps
    if (type === EVENTS.STEP_AFTER && action === ACTIONS.NEXT) {
      if (step?.target === '[data-tour="public-profile"]') {
        // Optional: Auto-navigate to profile page
        // navigate('/influencer/public-profile');
      } else if (step?.target === '[data-tour="rate-media-kit"]') {
        // Optional: Auto-navigate to rate page
        // navigate('/influencer/rate-media-kit');
      }
    }
  };

  const handleBookTraining = async (item) => {
    if (item.title == "Book Training") {
      if (hasPermission(10)) {
        try {
          const response = await appointmentsService.getAppointmentsLinks();
          if (response?.success) {
            window.open(
              response?.data?.booking_url,
              "_blank",
              "noopener,noreferrer"
            );
          }
        } catch (error) {
          console.error(
            "Error fetching appointment link:",
            parseToJson(decodeFromBase64(error.message))
          );
        }
      } else {
        setIsSubscribeModal(true);
      }
    } else {
      window.open(item.url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <>
      {/* React Joyride Tour Component */}
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showProgress
        scrollToFirstStep
        spotlightClicks
        disableOverlayClose
        callback={handleJoyrideCallback}
        styles={joyrideStyles}
        locale={{
          back: "Back",
          close: "Close",
          last: "Finish",
          next: "Next",
        }}
        floaterProps={{
          disableAnimation: false,
        }}
      />
      <SidebarGroup>
        {/* TEST BUTTON - Remove after testing */}
        {/* <button
          onClick={() => {
            setTourSteps([
              {
                target: "body",
                content: "Welcome! Let's complete your profile to get started. A complete profile helps brands discover you and increases your chances of getting campaigns.",
                placement: "center",
                disableBeacon: true,
                title: "🎉 Complete Your Profile",
              },
              {
                target: '[data-tour="public-profile"]',
                content: "Start by setting up your Public Profile. Add your bio, location, and other details to help brands learn more about you.",
                placement: "right",
                title: "📝 Public Profile",
                disableOverlay: true,
              },
              {
                target: '[data-tour="rate-media-kit"]',
                content: "Set up your rates and media kit to showcase your pricing and portfolio to potential brand partners.",
                placement: "right",
                title: "💰 Rate & Media Kit",
                disableOverlay: true,
              },
            ]);
            setRunTour(true);
          }}
          className="mb-3 mx-2 px-3 py-2 text-xs font-medium bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:from-emerald-600 hover:to-teal-600 transition-all duration-200 shadow-md hover:shadow-lg"
        >
          🧪 Test Tour
        </button> */}
        <SidebarMenu>
          {items?.map((item) => {
            const isActive =
              (item.url === "/" && location.pathname === "/") ||
              (item.url !== "/" &&
                location.pathname?.startsWith(item.url?.split("?")[0])) ||
              (item?.title === "Campaigns" &&
                location?.pathname?.includes("application"));

            const needsHighlight =
              userInfo &&
              ((item.title === "Public Profile" &&
                userInfo.i_profile_set === 0) ||
                (item.title === "Rate & Media Kit" && userInfo.rate_set === 0));

            // Generate data-tour key for targeting in Joyride
            const getTourKey = () => {
              if (item.title === "Public Profile") return "public-profile";
              if (item.title === "Rate & Media Kit") return "rate-media-kit";
              return null;
            };
            const tourKey = getTourKey();

            const handleClick = () => {
              if (item.externalUrL) {
                handleBookTraining(item);
              } else if (item.url) {
                navigate(item.url);
              }
            };

            return (
              <Collapsible
                key={item.title}
                asChild
                defaultOpen={isActive}
                className="group/collapsible"
              >
                <SidebarMenuItem
                  onClick={handleClick}
                  data-tour={tourKey}
                  className={`
                    w-full px-4 py-2.5 rounded-xl font-medium transition-all duration-200 cursor-pointer
                    ${
                      isActive && isBrand
                        ? "bg-white text-green-900 shadow-md"
                        : ""
                    }
                    ${
                      isActive && !isBrand
                        ? "bg-white text-emerald-700 shadow-md"
                        : ""
                    }
                    ${
                      !isActive && isBrand
                        ? "text-gray-500 hover:bg-white hover:shadow-sm"
                        : ""
                    }
                    ${
                      !isActive && !isBrand
                        ? "text-slate-600 hover:text-emerald-700 hover:bg-white hover:shadow-sm"
                        : ""
                    }
                  `}
                  style={
                    isActive && !isBrand
                      ? {
                          borderLeft: "3px solid #10b981",
                          background:
                            "linear-gradient(90deg, rgba(16, 185, 129, 0.08), rgba(255, 255, 255, 1))",
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                        }
                      : {}
                  }
                >
                  <SidebarMenuButton
                    tooltip={item.title}
                    className="!bg-transparent relative"
                  >
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.title}
                        className="h-5 w-5 rounded-full ring-2 ring-emerald-500/20"
                      />
                    ) : (
                      <item.icon
                        className={`h-5 w-5 transition-all duration-200
                          ${isActive && isBrand ? "text-green-900" : ""}
                          ${isActive && !isBrand ? "text-emerald-600" : ""}
                          ${!isActive && isBrand ? "text-gray-400" : ""}
                          ${!isActive && !isBrand ? "text-slate-400" : ""}
                        `}
                      />
                    )}
                    <span
                      className={`transition-all duration-200 ${
                        isActive && !isBrand ? "font-semibold" : ""
                      }`}
                    >
                      {item.title}
                    </span>
                    {needsHighlight && (
                      <div className="absolute top-1/2 -translate-y-1/2 right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </Collapsible>
            );
          })}
        </SidebarMenu>
      </SidebarGroup>

      {isSubscribeModal && (
        <ModalNotSubscribe
          goToPricing={() => navigate("/brand/pricing")}
          type="brand"
          // setAuth={setAuth}
          // setModal={setModal}
          // NotifModul={NotifModul}
          // setNotifModul={setModalNotification}
          // profiles={profiles}
          // closeCartPanel={closeCartPanel}
          // user={user}
          // cartItem={cartItem}
          title={"Direct Campaigns"}
          onClose={() => setIsSubscribeModal(false)}
          // background={"background_inflist.png"}
        />
      )}
    </>
  );
}
